// Die kleinere Fassung eines Fotos fürs Blatt (seit E3b, O77): im Browser,
// aus dem Foto selbst, als JPEG – die längste Seite höchstens DRUCK_KANTE.
// So wird das PDF kleiner (Manuels erstes hatte 42 MB). Reine Logik ohne
// React; was sie vom Browser braucht (Bild, Leinwand), kommt herein – so
// lässt sie sich ohne Browser prüfen.
//
// Seit der Code-Prüfung E3b (23.10.2026, Befund 1 und 4): Ohne Zeichenkontext
// gilt das Original – eine leere Leinwand ergäbe ein schwarzes Foto auf dem
// Papier. Bild und Leinwand werden gleich wieder freigegeben, und die Fotos
// einer Unterlage werden nacheinander umgerechnet, nicht alle zugleich:
// Safari begrenzt den Speicher aller Leinwände zusammen, und Manuels
// Unterlagen haben 40 Fotos und mehr.

/** So lang wird die längste Seite eines Fotos fürs Blatt höchstens – auf A4 reicht das weit (rund 400 dpi im Feld). */
export const DRUCK_KANTE = 1400;

/** Ein dekodiertes Foto – im Browser ein ImageBitmap. */
export interface DruckBild {
  readonly width: number;
  readonly height: number;
  close(): void;
}

/** Eine Leinwand – im Browser ein <canvas>. */
export interface DruckLeinwand<B> {
  width: number;
  height: number;
  getContext(art: "2d"): { drawImage(bild: B, x: number, y: number, breite: number, hoehe: number): void } | null;
  toBlob(fertig: (blob: Blob | null) => void, art: string, guete: number): void;
}

/** Was die Umrechnung vom Browser braucht – im Test Fälschungen. */
export interface DruckWerkzeug<B extends DruckBild> {
  bildAus(roh: Blob): Promise<B>;
  leinwand(): DruckLeinwand<B>;
}

/**
 * Die Druckfassung eines Fotos – oder null, dann gilt das Original: schon
 * klein genug, kein Zeichenkontext, kein Ergebnis, nicht kleiner geworden.
 * Fehler des Browsers (Bild lässt sich nicht dekodieren) gehen weiter; der
 * Aufrufer nimmt dann ebenfalls das Original.
 */
export async function druckfassungRechnen<B extends DruckBild>(roh: Blob, werkzeug: DruckWerkzeug<B>): Promise<Blob | null> {
  const bild = await werkzeug.bildAus(roh);
  let leinwand: DruckLeinwand<B> | null = null;
  try {
    const faktor = Math.min(1, DRUCK_KANTE / Math.max(bild.width, bild.height));
    if (faktor === 1) return null;
    const l = (leinwand = werkzeug.leinwand());
    l.width = Math.round(bild.width * faktor);
    l.height = Math.round(bild.height * faktor);
    const stift = l.getContext("2d");
    if (!stift) return null;
    stift.drawImage(bild, 0, 0, l.width, l.height);
    const klein = await new Promise<Blob | null>((fertig) => l.toBlob(fertig, "image/jpeg", 0.82));
    // Nur, wenn es wirklich kleiner ist – ein schon kleines Foto bliebe sonst größer.
    return klein && klein.size < roh.size ? klein : null;
  } finally {
    bild.close();
    if (leinwand) {
      leinwand.width = 0;
      leinwand.height = 0;
    }
  }
}

/**
 * Eins nach dem anderen: Jede Arbeit beginnt erst, wenn die vorige fertig ist
 * – auch wenn die vorige scheitert. Die Reihenfolge bleibt die des Einreihens.
 */
export function warteschlange(): <T>(arbeit: () => Promise<T>) => Promise<T> {
  let kette: Promise<unknown> = Promise.resolve();
  return <T>(arbeit: () => Promise<T>) => {
    const dran = kette.then(arbeit, arbeit);
    kette = dran.catch(() => undefined);
    return dran;
  };
}
